// Basic preview mask builder (FIRST PASS - fast, browser safe)
// This is NOT final RIP logic. This is just for UI preview behavior.

import { WhiteLayerSettings } from "../../types/whiteLayer";

/**
 * Converts image pixel data into a simple W1-style mask for preview
 * This is intentionally lightweight for browser use
 */
export function buildPreviewMask(
  imageData: ImageData,
  settings: WhiteLayerSettings
): ImageData {
  const { data, width, height } = imageData;

  const output = new ImageData(width, height);

  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const a = data[i + 3];

    // Skip fully transparent pixels
    if (a === 0) {
      output.data[i] = 255;
      output.data[i + 1] = 255;
      output.data[i + 2] = 255;
      output.data[i + 3] = 0;
      continue;
    }

    // --- STEP 1: base from alpha ---
    let alphaFactor = a / 255;

    // --- STEP 2: luminance (0 = dark, 1 = bright) ---
    const luminance =
      (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;

    // --- STEP 3: dark color reduction ---
    const darkReductionFactor =
      1 - (settings.darkReduction / 100) * (1 - luminance);

    // --- STEP 4: fade support ---
    const fadeBoost =
      1 + (settings.fadeSupport / 100) * (1 - alphaFactor);

    // --- STEP 5: combine ---
    let strength =
      alphaFactor *
      darkReductionFactor *
      fadeBoost *
      (settings.whiteStrength / 100);

    // --- STEP 6: noise cleanup ---
    if (strength < settings.noiseCleanup / 100) {
      strength = 0;
    }

    // Clamp
    strength = Math.max(0, Math.min(1, strength));

    // Convert to grayscale (inverted for W1 style preview)
    const gray = Math.round(255 * (1 - strength));

    output.data[i] = gray;
    output.data[i + 1] = gray;
    output.data[i + 2] = gray;
    output.data[i + 3] = 255;
  }

  return output;
}
